// Composer Studio — 노드 트리 walk 헬퍼 (Phase 3b-2 드래그용)
//
// 드래그 대상(= ComposerNode 배열의 원소: body / group.children)을 열거하고, 각 노드의
// 부모 컨테이너(body=null 또는 group id)와 형제 배열 내 index를 알아낸다.
// repeat.template(단일 그룹)·table cell 내부 content 노드는 이번(3b-2) 드래그 범위 외이므로
// "드롭 가능 컨테이너/드래그 가능 노드" 열거에서 제외한다(group/body만 컨테이너로 취급).

import type { ComposerNode } from '@safetyhub/report-composer';

/** 드래그 가능한 노드의 위치 정보 */
export interface DraggableEntry {
  node: ComposerNode;
  /** 부모 group id (body 직속이면 null) */
  parentId: string | null;
  /** 형제 배열 내 index */
  index: number;
}

/** 드롭 컨테이너(body 또는 group)의 정보 */
export interface ContainerEntry {
  /** body면 null, group이면 group id */
  parentId: string | null;
  /** 컨테이너의 자식 노드 배열(순서대로) */
  children: ComposerNode[];
}

/**
 * body / group.children를 재귀로 훑어 드래그 가능한 노드(=배열 원소)를 모두 열거한다.
 * table cell content / repeat.template 내부는 컨테이너로 취급하지 않으므로 그 안의 노드는 제외된다.
 */
export function listDraggables(body: ComposerNode[]): DraggableEntry[] {
  const out: DraggableEntry[] = [];
  const walk = (nodes: ComposerNode[], parentId: string | null) => {
    nodes.forEach((node, index) => {
      out.push({ node, parentId, index });
      if (node.kind === 'group' || node.kind === 'stack') {
        // group/stack 모두 자식 컨테이너 — 자식을 드래그 가능 노드로 열거.
        walk(node.children, node.id);
      }
      // repeat / table 내부는 드래그 범위 외 — 하위 walk 생략
    });
  };
  walk(body, null);
  return out;
}

/**
 * body + 모든 group을 드롭 컨테이너로 열거한다(빈 group도 포함 — 빈 그룹에 드롭 가능).
 */
export function listContainers(body: ComposerNode[]): ContainerEntry[] {
  const out: ContainerEntry[] = [{ parentId: null, children: body }];
  const walk = (nodes: ComposerNode[]) => {
    for (const node of nodes) {
      if (node.kind === 'group' || node.kind === 'stack') {
        // group/stack 모두 드롭 컨테이너(빈 컨테이너도 포함).
        out.push({ parentId: node.id, children: node.children });
        walk(node.children);
      }
    }
  };
  walk(body);
  return out;
}

/**
 * id 노드의 *직속 컨테이너*와 형제 배열 내 index. 셀 content 안 노드(직속 부모가 셀)까지 커버한다.
 *  - body: body 직속(최상위 흐름).
 *  - group: group/stack.children 안(parentId = 그 컨테이너 id).
 *  - cell: 표 셀 content 안(직속 부모가 셀; parentId 대신 tableId+row+col로 셀을 식별).
 *  - repeat-template: repeat.template(단일 group) 직속 자식. templateId(=group id)로 삽입 대상 특정.
 *    (반복 단위 자식의 복제/추가-뒤 위치를 그 template group 안으로 유지. group과 별개 kind라
 *     "밖으로 내보내기"(경계 넘김)는 자연히 제외된다.)
 */
export type NodeContainerLoc =
  | { kind: 'body'; index: number }
  | { kind: 'group'; parentId: string; index: number }
  | { kind: 'cell'; tableId: string; row: number; col: number; index: number }
  | { kind: 'branch-slot'; branchId: string; slotId: string; index: number }
  | { kind: 'repeat-template'; repeatId: string; templateId: string; index: number };

/**
 * body 전체(그룹/스택 children + 표 셀 content + repeat.template 포함)를 순회해 id 노드의 직속
 * 컨테이너와 형제 배열 내 index를 찾는다. 못 찾으면 null.
 *
 * parentIdOf(형제 판정)와 Ctrl+D 복제(같은 컨테이너 바로 뒤 삽입)의 공용 위치 조회 — 셀 안 노드도
 * 원본과 같은 셀 content에 삽입되도록 tableId/row/col을 함께 돌려준다.
 */
export function findNodeContainer(body: ComposerNode[], id: string): NodeContainerLoc | null {
  const search = (
    nodes: ComposerNode[],
    container: (index: number) => NodeContainerLoc,
  ): NodeContainerLoc | null => {
    const idx = nodes.findIndex((n) => n.id === id);
    if (idx !== -1) return container(idx);
    for (const n of nodes) {
      if (n.kind === 'group' || n.kind === 'stack') {
        const r = search(n.children, (i) => ({ kind: 'group', parentId: n.id, index: i }));
        if (r) return r;
      } else if (n.kind === 'branch') {
        for (const slot of n.branches) {
          const r = search(slot.children, (i) => ({
            kind: 'branch-slot',
            branchId: n.id,
            slotId: slot.id,
            index: i,
          }));
          if (r) return r;
        }
      } else if (n.kind === 'table') {
        for (const cell of n.cells) {
          if (!cell.content) continue;
          const r = search(cell.content, (i) => ({
            kind: 'cell',
            tableId: n.id,
            row: cell.row,
            col: cell.col,
            index: i,
          }));
          if (r) return r;
        }
      } else if (n.kind === 'repeat') {
        // repeat.template(단일 group) 직속 자식은 repeat-template loc, 그 안 중첩 컨테이너는
        // 재귀 search가 group/branch-slot/cell로 알아서 좁힌다(template group id로 삽입 대상 특정).
        const r = search(n.template.children, (i) => ({
          kind: 'repeat-template',
          repeatId: n.id,
          templateId: n.template.id,
          index: i,
        }));
        if (r) return r;
      }
    }
    return null;
  };
  return search(body, (i) => ({ kind: 'body', index: i }));
}

/** walkNodeTree visit 콜백에 전달되는 부모(직속 컨테이너) 정보. */
export interface WalkParentInfo {
  /** 직속 컨테이너 종류. */
  container: 'body' | 'group' | 'stack' | 'cell' | 'repeat-template' | 'branch-slot';
  /** 부모 노드 id(body 직속이면 null; cell/repeat-template/branch-slot이면 표/repeat/branch 노드 id). */
  parentId: string | null;
}

/** walkNodeTree 하강 정책 옵션(기본은 가장 흔한 정책 = 전부 하강). */
export interface WalkNodeTreeOptions {
  /** table cell.content 하위로 내려갈지(기본 true). */
  includeCellContent?: boolean;
  /** repeat.template(단일 group) 하위로 내려갈지(기본 true). */
  includeRepeatTemplate?: boolean;
}

/**
 * 범용 노드 트리 visitor 프리미티브 — body / group·stack.children를 재귀 순회하며 각 노드에 visit을 호출한다.
 * group/stack.children는 **항상** 하강한다. table cell.content · repeat.template 하강은 opts로 제어하며
 * 기본값은 둘 다 true(가장 흔한 정책 = 모든 노드 순회: collectAllNodeIds/배지 오버레이/findNodeById와 동일 범위).
 * visit이 `false`를 반환하면 그 노드의 하위를 스킵한다(그 외 반환값은 계속 하강).
 *
 * 하강 범위별 소비처(현행): 전부(cell+repeat)=collectAllNodeIds·바인딩/조건 배지 / cell만(repeat 제외)=findNodeContainer
 * / 둘 다 제외(group·stack만)=listDraggables·listContainers·DragLayer 핸들. 각 소비처는 자기 정책을 opts로 표현한다.
 */
export function walkNodeTree(
  nodes: ComposerNode[],
  visit: (node: ComposerNode, parent: WalkParentInfo) => void | false,
  opts?: WalkNodeTreeOptions,
): void {
  const includeCell = opts?.includeCellContent ?? true;
  const includeRepeat = opts?.includeRepeatTemplate ?? true;
  const walk = (list: ComposerNode[], parent: WalkParentInfo) => {
    for (const n of list) {
      if (visit(n, parent) === false) continue; // 하위 스킵
      if (n.kind === 'group' || n.kind === 'stack') {
        walk(n.children, { container: n.kind, parentId: n.id });
      } else if (n.kind === 'branch') {
        // 슬롯 children은 group.children처럼 항상 하강(모든 노드 순회 정책 = id 수집·배지 대상).
        for (const slot of n.branches) {
          walk(slot.children, { container: 'branch-slot', parentId: n.id });
        }
      } else if (n.kind === 'table') {
        if (includeCell) {
          for (const cell of n.cells) {
            if (cell.content) walk(cell.content, { container: 'cell', parentId: n.id });
          }
        }
      } else if (n.kind === 'repeat') {
        if (includeRepeat) walk([n.template], { container: 'repeat-template', parentId: n.id });
      }
    }
  };
  walk(nodes, { container: 'body', parentId: null });
}

/**
 * body 전체를 재귀 순회해 모든 노드 id를 평탄화한다.
 * 하강 정책: group/stack.children · table cell.content · repeat.template 전부(walkNodeTree 기본값).
 * (id 충돌 회피·패딩 오버레이 대상 열거 등에 공용으로 쓴다.)
 */
export function collectAllNodeIds(nodes: ComposerNode[]): string[] {
  const ids: string[] = [];
  walkNodeTree(nodes, (n) => {
    ids.push(n.id);
  });
  return ids;
}
